import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { useRangeKey } from '../TimeRangeControl';
import { fetchGeo } from '../api';
import type { GeoPayload } from '../types';
import { BuiltAt, QUERY_OPTIONS, TabStatus } from './_shared';

type FlatCell = { actor: string; infra: string; n: number };

function aggregateGrid(yearly: GeoPayload['yearly_grid'], year: string | null): FlatCell[] {
  const totals = new Map<string, number>(); // "actor::infra" → n
  const years = year ? [year] : Object.keys(yearly);
  for (const y of years) {
    const cells = yearly[y] || {};
    for (const [key, n] of Object.entries(cells)) {
      totals.set(key, (totals.get(key) || 0) + n);
    }
  }
  return Array.from(totals.entries()).map(([key, n]) => {
    const [actor, infra] = key.split('::', 2);
    return { actor, infra, n };
  });
}

function GeoMatrix({ cells }: { cells: FlatCell[] }) {
  // Build sorted axes from the cells we have.
  const actorTotals = new Map<string, number>();
  const infraTotals = new Map<string, number>();
  for (const c of cells) {
    actorTotals.set(c.actor, (actorTotals.get(c.actor) || 0) + c.n);
    infraTotals.set(c.infra, (infraTotals.get(c.infra) || 0) + c.n);
  }
  const actors = Array.from(actorTotals.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([k]) => k)
    .slice(0, 15);
  const infras = Array.from(infraTotals.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([k]) => k)
    .slice(0, 15);

  if (actors.length === 0 || infras.length === 0) {
    return <div className="trends-chart-empty">No actor × infrastructure pairs.</div>;
  }

  const cellMap = new Map<string, number>(cells.map((c) => [`${c.actor}::${c.infra}`, c.n]));
  const maxN = Math.max(...cells.map((c) => c.n), 1);

  return (
    <div className="trends-geo-matrix-wrap">
      <table className="trends-geo-matrix">
        <thead>
          <tr>
            <th className="trends-geo-corner">
              <span>actor ↓ / infra →</span>
            </th>
            {infras.map((c) => (
              <th key={c}>
                <Link to={`/country/${encodeURIComponent(c)}`} className="trends-entity-link">
                  {c}
                </Link>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {actors.map((a) => (
            <tr key={a}>
              <th>
                <Link to={`/country/${encodeURIComponent(a)}`} className="trends-entity-link">
                  {a}
                </Link>
              </th>
              {infras.map((c) => {
                const n = cellMap.get(`${a}::${c}`) || 0;
                const intensity = n ? Math.min(1, n / maxN) : 0;
                return (
                  <td
                    key={c}
                    style={{
                      background: n
                        ? `rgba(108, 140, 255, ${0.10 + intensity * 0.85})`
                        : undefined,
                    }}
                    title={`${a} → ${c}: ${n}`}
                  >
                    {n || ''}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function GeoTab() {
  const range = useRangeKey();
  const [selectedYear, setSelectedYear] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['trends', 'geo', range],
    queryFn: () => fetchGeo(range),
    ...QUERY_OPTIONS,
  });

  const years = useMemo(
    () => (q.data ? Object.keys(q.data.yearly_grid).sort() : []),
    [q.data],
  );

  const cells = useMemo(
    () => (q.data ? aggregateGrid(q.data.yearly_grid, selectedYear) : []),
    [q.data, selectedYear],
  );

  return (
    <section className="trends-tab-content trends-geo">
      <TabStatus
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        data={q.data}
        emptyHint="Geo needs ThreatActor.country and asn_enrichment.country. Both feed the trends_snapshot Dagster asset."
      >
        {q.data && (
          <>
            <BuiltAt at={q.data.built_at} />
            <div className="trends-controls-row">
              <h2 className="trends-section-title">
                Actor origin × infrastructure country
                {selectedYear ? ` — ${selectedYear}` : ` — ${years.length} years`}
              </h2>
              <div className="trends-chip-filters">
                <button
                  type="button"
                  className={`trends-chip${selectedYear === null ? ' active' : ''}`}
                  onClick={() => setSelectedYear(null)}
                >
                  All
                </button>
                {years.map((y) => (
                  <button
                    key={y}
                    type="button"
                    className={`trends-chip${selectedYear === y ? ' active' : ''}`}
                    onClick={() => setSelectedYear(y)}
                  >
                    {y}
                  </button>
                ))}
              </div>
            </div>
            <GeoMatrix cells={cells} />
          </>
        )}
      </TabStatus>
    </section>
  );
}
